import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { AuthContext } from 'utils/AuthContext';

import { syncProfile } from 'store/slices/adminSlice';
import {
  callArchiveDrChronoJobEvent,
  getDrChronoEventHistoryDetails,
} from 'store/slices/patientInfoSlice';

import { useAppDispatch } from 'store/hooks';
import { toast } from 'react-toastify';
import { ROUTE } from 'route/RouteEnums';
import { Link } from 'react-router-dom';
import { arch } from 'os';
import ApplicationHealthDashboard from './ApplicationHealthDashboard';
import { validateSession } from 'services/validateSession';

// This file intentionally avoids using @mui/x-data-grid-pro or other "prod" grid libs.
// It implements a simple table with expand/collapse (accordion) rows using MUI core only.

export default function PatientJobsTable() {
  const { user, setUserDetails } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const [patientData, setPatientData] = useState<any>([]);

  const formatDate = (val: number) => {
    try {
      return new Date(val).toLocaleString();
    } catch {
      return String(val);
    }
  };

  const splitFailure = (msg?: string) => {
    if (!msg) return [];
    // split before sequences like "0 :" or "1 :" etc and trim
    return msg
      .split(/(?=\d+\s*[:])/g)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  // allow multiple rows expanded at once
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    getDrChronoEventHistory();
  }, []);

  function getDrChronoEventHistory() {
    dispatch(getDrChronoEventHistoryDetails({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setLoading(false);
          setPatientData(response?.data);
        }
      });
  }
  function jobRetry(patientId: number) {
    dispatch(syncProfile(patientId))
      .unwrap()
      .then((res: any) => {
        if (res.status === 200) {
          toast('Job retried successfully', {
            type: 'success',
            icon: true,
          });

          // ⬇️ REMOVE ROW FROM TABLE
          setPatientData((prev: any) => ({
            ...prev,
            pageResults: prev.pageResults.filter((row: any) => row.patientId !== patientId),
          }));
        }
      });
  }

  function archiveJob(jobId: number, patientId: number) {
    dispatch(callArchiveDrChronoJobEvent({ jobId, patientId }))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setLoading(false);
          toast('Job archived successfully', {
            type: 'success',
            icon: true,
          });
        }
      });
  }
  function copyJob(job: any) {
    if (!navigator.clipboard) return alert('Clipboard not supported');
    navigator.clipboard.writeText(JSON.stringify(job, null, 2));
  }
  const rowSelect = async (params: any) => {
    await validateSession();
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${params.row.patientId}`, '_blank');
    const data = {
      ...user,
      patientId: params.row.patientId,
    };
    setUserDetails(data);
  };

  return (
    <Container maxWidth='lg'>
      <Grid item xs={12}>
        <Typography
          variant='h5'
          position={'relative'}
          width={'fit-content'}
          sx={{ paddingTop: 6, textTransform: 'capitalize' }}
        >
          Patient Sync Jobs
        </Typography>
      </Grid>
      <Grid item xs={12} sx={{ padding: 2, borderRadius: 2 }}>
        <Paper elevation={0} sx={{ width: '100%' }}>
          <TableContainer>
            <Table stickyHeader size='small' aria-label='patient-jobs-table'>
              <TableHead>
                <TableRow sx={{ boxShadow: '0px 4px 4px -2px rgba(0,0,0,0.2)', mb: 2 }}>
                  <TableCell sx={{ width: 72 }} />
                  <TableCell>Patient ID</TableCell>
                  <TableCell>Patient Name</TableCell>
                  <TableCell>Jobs</TableCell>
                  <TableCell align='center'>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {patientData?.pageResults?.map((row: any) => {
                  const expanded = expandedIds.includes(row.patientId as number);
                  return (
                    <React.Fragment key={row.patientId}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size='small'
                            onClick={() => toggleExpand(row.patientId as number)}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                          >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>

                        <TableCell>
                          <span
                            onClick={() => rowSelect({ row })}
                            style={{
                              color: '#1976d2',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            {row.patientId}
                          </span>
                        </TableCell>
                        <TableCell>{row.patientName}</TableCell>
                        <TableCell>{row.jobCount}</TableCell>
                        <TableCell align='center'>
                          <IconButton
                            size='small'
                            onClick={() => jobRetry(row.patientId)}
                            title='Retry'
                          >
                            <ReplayIcon fontSize='small' color='warning' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {expanded ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Collapse in={expanded} timeout='auto' unmountOnExit>
                              <Box sx={{ margin: 2, borderRadius: 1, p: 1 }}>
                                {(!row.jobs || row.jobs.length === 0) && (
                                  <Typography variant='body2'>No jobs available</Typography>
                                )}
                                {row.jobs &&
                                  row.jobs.map((job: any, idx: number) => (
                                    <Paper
                                      key={job.id || idx}
                                      sx={{
                                        p: 2,
                                        my: 1,
                                        borderRadius: 1,
                                        border: '1px solid #95a984ff',
                                      }}
                                      elevation={5}
                                    >
                                      <Stack
                                        direction={{ xs: 'column', sm: 'row', md: 'row' }}
                                        spacing={2}
                                        alignItems='center'
                                        justifyContent='space-between'
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                          }}
                                        >
                                          <Typography variant='body2'>
                                            <strong>Job No:</strong>&nbsp;
                                            <span style={{ fontFamily: 'monospace' }}>
                                              {job.jobId}
                                            </span>
                                          </Typography>
                                          <Typography variant='body2'>
                                            <strong>Event Name:</strong>&nbsp;{job.eventType}
                                          </Typography>

                                          <Typography variant='body2'>
                                            <strong>Retry Count:</strong>&nbsp;{job.retryCount}
                                          </Typography>
                                          <Typography
                                            variant='body2'
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                          >
                                            <strong>Job Status:</strong>
                                            <Chip
                                              label={job.status}
                                              size='small'
                                              sx={{ height: 22 }}
                                            />
                                          </Typography>
                                          <Typography variant='body2'>
                                            <strong>Created:</strong>&nbsp;
                                            {formatDate(job.createdDate)}
                                          </Typography>
                                        </Box>

                                        {/* small action icons */}
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                          <IconButton
                                            size='small'
                                            onClick={() => copyJob(job)}
                                            title='Copy'
                                          >
                                            <ContentCopyIcon fontSize='small' color='success' />
                                          </IconButton>
                                        </Stack>
                                      </Stack>

                                      {/* Divider */}
                                      <Divider sx={{ my: 1 }} />

                                      {/* Failure message - split into numbered paragraphs (function splitFailure required) */}
                                      <Box>
                                        <Typography variant='body2' sx={{ mb: 0.5 }}>
                                          <strong>Failure:</strong>
                                        </Typography>

                                        <Box sx={{ pl: 4, backgroundColor: '#deedc4ff' }}>
                                          {splitFailure(job.failureMessage).map(
                                            (part: string, i: number) => (
                                              <Typography
                                                key={i}
                                                variant='body2'
                                                sx={{
                                                  mb: 0.5,
                                                  whiteSpace: 'pre-wrap',
                                                  wordBreak: 'break-word',
                                                }}
                                              >
                                                {part}
                                              </Typography>
                                            )
                                          )}
                                        </Box>
                                      </Box>
                                    </Paper>
                                  ))}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Container>
  );
}
